import type { WebContact } from './contact-book';

// One page of the Outreach Contacts tab's read: the browse ("head") plus a page of the WHOLE people
// book. Kept apart from `contact-book.ts`, and free of runtime imports, so the parse is unit-testable
// without dragging the app's dependency graph into jest (see this package's CLAUDE.md, "Tests").

/**
 * What `/api/contact-book` answers when the Contacts tab asks for the whole book.
 *
 * `contacts` is the browse — the people behind the caller's recent threads, exactly what every other
 * contacts surface reads. It is EMPTY on a later page: a `bookCursor` request reads only the book, so
 * the caller keeps the head it already holds rather than replacing it with nothing.
 *
 * `book` is everyone in the people book, messaged or not, including the people already in `contacts`
 * (the server cannot filter a later page against a browse it did not read). `bookTail` drops the overlap
 * and orders the rest by name.
 */
export interface WebContactBookPage {
  contacts: WebContact[];
  book: WebContact[];
  /** Absent on the last page — and on every page from a server that predates the book. */
  bookNextCursor?: string;
  /**
   * Page 1 of the book could not be read; `contacts` is still the whole head. NOT the same as an empty
   * book on its last page — the tab keeps whatever book it holds, and with nothing to show it is the
   * read error and its Retry, never "No contacts yet". Shared 1:1 with iOS/Android `bookUnavailable`.
   */
  bookUnavailable: boolean;
}

/**
 * Parse the route's JSON into a page. Tolerant of an older server, which answers the same request
 * with `contacts` alone: no `book` is an empty book and no cursor is the last page, so the tab falls
 * back to exactly what it showed before.
 *
 * A row with no card cannot be drawn or opened, so it is dropped here, as `fetchContacts` drops it.
 * Shared 1:1 with iOS `SymphonyAPI.parseContactsBookPage` and Android `SymphonyApi.parseContactsBookPage`.
 */
export function parseContactBookPage(data: unknown): WebContactBookPage {
  const body = (data ?? {}) as { contacts?: WebContact[]; book?: WebContact[]; bookNextCursor?: string; bookUnavailable?: boolean };
  const rows = (list: WebContact[] | undefined) => (Array.isArray(list) ? list : []).filter((entry) => !!entry?.contact);
  const cursor = typeof body.bookNextCursor === 'string' && body.bookNextCursor ? body.bookNextCursor : undefined;
  return { contacts: rows(body.contacts), book: rows(body.book), bookNextCursor: cursor, bookUnavailable: body.bookUnavailable === true };
}
